import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";
import { PageHero } from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { format, parseISO } from "date-fns";
import { de as deLocale, fr as frLocale } from "date-fns/locale";
import { Check, Clock, CalendarDays } from "lucide-react";

export default function ConsultationPage() {
  const { t, lang } = useLanguage();
  const locale = lang === "fr" ? frLocale : deLocale;
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  useSeo("Kennenlerngespräch buchen", "Réserver un entretien", "Bucht ein unverbindliches Kennenlerngespräch mit Our Promise Events.", "Réservez un entretien de découverte sans engagement avec Our Promise Events.");

  const loadSlots = () => {
    setLoading(true);
    api.get("/slots").then((r) => setSlots(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(loadSlots, []);

  // group slots by date
  const grouped = slots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const book = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error(t("consultation.chooseSlot")); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/consultations", { slot_id: selected.id, ...form, lang });
      setResult(data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
      loadSlots();
    } finally {
      setSubmitting(false);
    }
  };

  const downloadIcs = () => {
    if (!result?.ics) return;
    const blob = new Blob([result.ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kennenlerngespraech.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (result) {
    return (
      <div data-testid="consultation-page">
        <PageHero overline={t("consultation.overline")} title={t("consultation.successTitle")} />
        <section className="bg-cream py-24 px-6">
          <div data-testid="consultation-success" className="max-w-xl mx-auto bg-secondary/40 border border-gold p-12 text-center">
            <div className="w-14 h-14 mx-auto mb-6 rounded-full border border-gold flex items-center justify-center text-gold"><Check size={22} /></div>
            <p className="font-heading text-2xl mb-2">{result.date} · {result.time}</p>
            <p className="text-ink-muted leading-relaxed mb-8">{t("consultation.success")}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={downloadIcs} data-testid="consultation-ics" className="bg-ink text-cream px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-gold transition-colors">{t("consultation.downloadIcs")}</button>
              <button onClick={() => { setResult(null); setSelected(null); setForm({ name: "", email: "", phone: "", message: "" }); loadSlots(); }} className="border border-ink px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-ink hover:text-cream transition-colors">{t("consultation.newBooking")}</button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="consultation-page">
      <PageHero overline={t("consultation.overline")} title={t("consultation.title")} subtitle={t("consultation.sub")} />

      <section className="bg-cream py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <p className="text-center text-ink-muted">{t("common.loading")}</p>
          ) : slots.length === 0 ? (
            <div className="text-center max-w-lg mx-auto" data-testid="consultation-no-slots">
              <CalendarDays size={40} className="mx-auto text-gold mb-6" strokeWidth={1} />
              <p className="text-ink-muted leading-relaxed">{t("consultation.noSlots")}</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Slots */}
              <Reveal>
                <p className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-6">{t("consultation.chooseSlot")}</p>
                <div className="space-y-6">
                  {Object.keys(grouped).sort().map((d) => (
                    <div key={d}>
                      <p className="font-heading text-xl mb-3">{format(parseISO(d), "EEEE, d. MMMM yyyy", { locale })}</p>
                      <div className="flex flex-wrap gap-2">
                        {grouped[d].map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            data-testid={`slot-${s.id}`}
                            onClick={() => setSelected(s)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm border transition-colors ${selected?.id === s.id ? "border-gold bg-gold text-ink" : "border-border hover:border-ink"}`}
                          >
                            <Clock size={14} /> {s.time}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>

              {/* Form */}
              <Reveal delay={120}>
                <form onSubmit={book} data-testid="consultation-form" className="bg-card border border-border p-8 space-y-6">
                  {selected && (
                    <p className="text-sm bg-secondary/50 px-4 py-3 border border-gold/40">
                      {t("consultation.selected")}: <strong>{format(parseISO(selected.date), "PPP", { locale })} · {selected.time}</strong>
                    </p>
                  )}
                  <Field label={t("consultation.name")} value={form.name} onChange={set("name")} testId="consultation-name" required />
                  <Field label={t("consultation.email")} type="email" value={form.email} onChange={set("email")} testId="consultation-email" required />
                  <Field label={t("consultation.phone")} value={form.phone} onChange={set("phone")} testId="consultation-phone" required />
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{t("consultation.message")}</label>
                    <textarea data-testid="consultation-message" value={form.message} onChange={set("message")} rows={3} className="w-full border-b border-border bg-transparent py-3 focus:outline-none focus:border-ink resize-none" />
                  </div>
                  <button type="submit" disabled={submitting || !selected} data-testid="consultation-submit" className="w-full bg-ink text-cream py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-gold transition-colors disabled:opacity-50">
                    {submitting ? t("common.sending") : t("consultation.book")}
                  </button>
                </form>
              </Reveal>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={onChange} required={required} className="w-full border-b border-border bg-transparent py-3 focus:outline-none focus:border-ink transition-colors" />
    </div>
  );
}
